import React, { useState } from "react";
import style from "./event-card.module.css";
import { TEvent, TEventHost } from "../../types";
import AppHelper from "../../utils/helper";
import { decodeHtmlEntities, toSafeHtml } from "../../utils/html";
import LocationIcon from "../icons/location-icon";
import MetroIcon from "../icons/metro-icon";
import TimeIcon from "../icons/time-icon";
import PriceIcon from "../icons/price-icon";
import ParticipantsFull from "../participants/participants-full";
import Heading from "../heading/heading";
import EventOrderModal from "./event-order-modal";
import EventHead from "./event-head";
import EventHostCard from "./event-host-card";
import EventHostModal from "./event-host-modal";

interface Props {
    event: TEvent;
}

const EventCardFull = ({ event }: Props) => {
    const titleText = decodeHtmlEntities(event.title);
    const [isOrderOpen, setIsOrderOpen] = useState(false);
    const [selectedHost, setSelectedHost] = useState<TEventHost | null>(null);
    const hosts = event.hosts?.length ? event.hosts : event.host ? [event.host] : [];
    const descriptionBlocks = event.description_blocks?.filter((block) => (
        Boolean(block.title?.trim()) || Boolean(block.description?.trim())
    )) ?? [];
    const metroStation = event.metro_station?.trim();
    const location = event.location?.trim();

    const handleEventClickOrder = (_eventId: number) => {
        setIsOrderOpen(true);
    };



    const startTime = AppHelper.formatTime(event.time_start);
    const endTime = AppHelper.formatTime(event.time_end);

    let timeDisplay = "";
    if (startTime && endTime) {
        timeDisplay = `${startTime}-${endTime}`;
    } else if (startTime) {
        timeDisplay = `с ${startTime}`;
    }

    const startDateTime = AppHelper.formatDateTimeRuLong(
        event.date_start,
        event.time_start,
    );
    const endDateTime = AppHelper.formatDateTimeRuLong(
        event.date_end ?? event.date_start,
        event.time_end,
    );

    const isFinished = event.is_finished;
    const showInfo = false;

    return (
        <>
            <Heading tpl="new" share={"e_" + event.id}>Детали <span className="my-text-primary">события</span></Heading>
            <div className={style.cardFull}>
                <div className={style.box}>
                    <EventHead event={event} />
                    <div className={style.content}>
                        <h2 className={style.title}>{titleText}</h2>
                        <div className={style.infoPanel}>
                            {(metroStation || location) && (
                                <div className={style.eventPlace}>
                                    {metroStation && (
                                        <div className={style.location}>
                                            <MetroIcon />
                                            <span>{metroStation}</span>
                                        </div>
                                    )}
                                    {location && (
                                        <div className={metroStation ? style.locationMuted : style.location}>
                                            <LocationIcon />
                                            <span>{location}</span>
                                        </div>
                                    )}
                                </div>
                            )}
                            {timeDisplay && (
                                <div className={style.time}>
                                    <TimeIcon />
                                    <span>{timeDisplay}</span>
                                </div>
                            )}
                            <div className={style.price}>
                                <PriceIcon />{" "}
                                <span>{AppHelper.formatPrice(event.price)}</span>
                            </div>
                        </div>

                        <div className={style.fullContent}>
                            {showInfo && (
                                <>
                                    <h3 className={style.h3}>Информация</h3>
                                    <div className={style.infoParams}>
                                        <div className={style.infoParamRow}>
                                            <div className={style.titleParam}>Начало:</div>
                                            <div>{startDateTime}</div>
                                        </div>
                                        <div className={style.infoParamRow}>
                                            <div className={style.titleParam}>Конец:</div>
                                            <div>{endDateTime}</div>
                                        </div>
                                        <div className={style.infoParamRow}>
                                            <div className={style.titleParam}>Адрес:</div>
                                            <div>{event.location}</div>
                                        </div>
                                        {event.metro_station && (
                                            <div className={style.infoParamRow}>
                                                <div className={style.titleParam}>Метро:</div>
                                                <div>{event.metro_station}</div>
                                            </div>
                                        )}
                                    </div>
                                </>)}
                            <h3 className={style.h3}>О мероприятии</h3>
                            <div
                                className={style.intro}
                                dangerouslySetInnerHTML={toSafeHtml(event.content)}
                            />
                            {descriptionBlocks.length > 0 && (
                                <div className={style.descriptionBlocks}>
                                    {descriptionBlocks.map((block, index) => (
                                        <details
                                            className={style.descriptionBlock}
                                            key={`${block.title}-${index}`}
                                            open={!block.is_collapsed}
                                        >
                                            <summary className={style.descriptionBlockTitle}>
                                                {decodeHtmlEntities(block.title)}
                                            </summary>
                                            {block.description && (
                                                <div
                                                    className={style.descriptionBlockText}
                                                    dangerouslySetInnerHTML={toSafeHtml(block.description)}
                                                />
                                            )}
                                        </details>
                                    ))}
                                </div>
                            )}
                            {hosts.length > 0 && (
                                <>
                                    <h3 className={style.h3}>Команда мероприятия</h3>
                                    {hosts.map((host) => (
                                        <EventHostCard
                                            key={`${event.id}-${host.id}-${host.role ?? "no-role"}`}
                                            host={host}
                                            title={host.role ?? undefined}
                                            full
                                            onClick={setSelectedHost}
                                        />
                                    ))}
                                </>
                            )}
                            <ParticipantsFull event={event} />
                        </div>


                        {!event.is_registered ? (
                            !isFinished && (
                                <div className={style.boxRecordLink}>
                                    <button
                                        onClick={() => handleEventClickOrder(event.id)}
                                        className="btn-link btn-link-full"
                                    >
                                        Записаться
                                    </button>
                                </div>
                            )
                        ) : (
                            <div className={style.registeredMessage}>
                                Вы уже записаны на это мероприятие
                            </div>
                        )}

                    </div>
                </div>
            </div>

            {!event.is_registered && !isFinished && (
                <EventOrderModal
                    event={event}
                    isOpen={isOrderOpen}
                    onClose={() => setIsOrderOpen(false)}
                />
            )}

            <EventHostModal
                host={selectedHost}
                isOpen={Boolean(selectedHost)}
                onClose={() => setSelectedHost(null)}
            />
        </>
    );
};

export default EventCardFull;
